// Refine bootstrap for the cc-os staff portal.
//
// Wave 4 wires:
//   - Refine + Supabase data provider.
//   - Refine authProvider (see lib/auth.ts).
//   - All Refine resources registered (Wave 1-3 entities) for routing.
//   - <RefineKbarProvider> only (no <RefineKbar /> auto-registration —
//     CommandPalette.tsx supplies the full set of Cmd-K entries explicitly).
//   - Routes for every Wave 3 page (samples, qa, shipments, quotes, costings,
//     stock-offers, developments, meetings) plus existing Wave 1-2 routes.

import { Refine, Authenticated } from "@refinedev/core";
import routerProvider, { CatchAllNavigate } from "@refinedev/react-router";
import { dataProvider } from "@refinedev/supabase";
import { RefineKbarProvider } from "@refinedev/kbar";
import { Navigate, Outlet, Route, Routes } from "react-router";
import { Toaster } from "sonner";
import { lazy, Suspense } from "react";
import { Library } from "lucide-react";

import { Layout } from "./components/Layout";
import { CommandPalette } from "./components/CommandPalette";
import { MfaGate } from "./components/MfaGate";
import { SampleCoordOnlyGuard } from "./components/SampleCoordOnlyGuard";
import { Loading } from "./components/Loading";
import { authProvider } from "./lib/auth";
import { supabase } from "./lib/supabase";
import { useVersionWatcher } from "./hooks/useVersionWatcher";

// ── Eager (critical-path) routes ─────────────────────────────────────────────
// Auth pages + the post-login dashboard + the most-used list pages
// (orders, customers, suppliers) ship in the main bundle so first paint
// after login is instant. Everything else is React.lazy()'d below so it
// loads on demand and stays out of the initial chunk.
import { LoginPage } from "./pages/login";
import { OAuthConsentPage } from "./pages/oauth/consent";
import { ResetPasswordPage } from "./pages/reset-password";
import { SetPasswordPage } from "./pages/set-password";
import { DashboardPage } from "./pages/dashboard";
import { MfaChallengePage } from "./pages/auth/mfa-challenge";
import { MfaEnrollPage } from "./pages/auth/mfa-enroll";
import { OrdersListPage } from "./pages/orders";
import { ActionsInboxPage } from "./pages/actions";
import { CustomersListPage } from "./pages/customers";
import { SuppliersListPage } from "./pages/suppliers";
// Note: pages under ./pages/meetings/** and ./pages/library/** are loaded
// via the defensiveLazy mechanism below (vite glob). They MUST NOT be
// statically imported here — vite warns when a module is imported both
// statically and dynamically (it can't split it into a separate chunk).

// ── Lazy (deferred) routes ──────────────────────────────────────────────────
// Each `React.lazy(...)` becomes its own chunk. Wrapping these in <Suspense>
// at the route element below prevents them from inflating the main bundle.
//
// Rule of thumb: if a page is hit on most sessions (the inboxes + the lists
// above) keep it eager; otherwise lazy-load it. Detail pages, dashboards,
// admin/finance, docs, and forms all sit behind a click and only matter
// once the user actually navigates to them.

// Wave 2/3 — detail pages + forms (deferred behind first navigation).
const OrderDetailPage = lazy(() =>
  import("./pages/orders/[id]").then((m) => ({ default: m.OrderDetailPage })),
);
const NewOrderForm = lazy(() =>
  import("./pages/orders/new").then((m) => ({ default: m.NewOrderForm })),
);
const ActionDetailPage = lazy(() =>
  import("./pages/actions/[code]").then((m) => ({ default: m.ActionDetailPage })),
);
const CustomerProfilePage = lazy(() =>
  import("./pages/customers/[id]").then((m) => ({ default: m.CustomerProfilePage })),
);
const FormsHubPage = lazy(() =>
  import("./pages/forms").then((m) => ({ default: m.FormsHubPage })),
);
const NewCustomerForm = lazy(() =>
  import("./pages/forms/new-customer").then((m) => ({ default: m.NewCustomerForm })),
);
const LogMeetingForm = lazy(() =>
  import("./pages/forms/log-meeting").then((m) => ({ default: m.LogMeetingForm })),
);
const NewSampleForm = lazy(() =>
  import("./pages/forms/new-sample").then((m) => ({ default: m.NewSampleForm })),
);
const QaResultForm = lazy(() =>
  import("./pages/forms/qa-result").then((m) => ({ default: m.QaResultForm })),
);
const NewQuoteForm = lazy(() =>
  import("./pages/forms/new-quote").then((m) => ({ default: m.NewQuoteForm })),
);
const NewPoForm = lazy(() =>
  import("./pages/forms/new-po").then((m) => ({ default: m.NewPoForm })),
);

// [Epic #1063] Phase 1b (#1065): the Supplier POs list + detail. The PPO
// document itself is rendered by the generate-po Edge Function; these pages
// are the only place a PO has ever been visible in the portal.
const SupplierPosListPage = lazy(() =>
  import("./pages/supplier-pos").then((m) => ({ default: m.SupplierPosListPage })),
);
const SupplierPoDetailPage = lazy(() =>
  import("./pages/supplier-pos/[id]").then((m) => ({ default: m.SupplierPoDetailPage })),
);
const SamplesListPage = lazy(() =>
  import("./pages/samples").then((m) => ({ default: m.SamplesListPage })),
);
const SampleDetailPage = lazy(() =>
  import("./pages/samples/[id]").then((m) => ({ default: m.SampleDetailPage })),
);
const QaListPage = lazy(() =>
  import("./pages/qa").then((m) => ({ default: m.QaListPage })),
);
const QaDetailPage = lazy(() =>
  import("./pages/qa/[id]").then((m) => ({ default: m.QaDetailPage })),
);
const QaPendingPage = lazy(() =>
  import("./pages/qa/pending").then((m) => ({ default: m.QaPendingPage })),
);
const ShipmentsListPage = lazy(() =>
  import("./pages/shipments").then((m) => ({ default: m.ShipmentsListPage })),
);
const ShipmentDetailPage = lazy(() =>
  import("./pages/shipments/[id]").then((m) => ({ default: m.ShipmentDetailPage })),
);
const PendingDeliveryPage = lazy(() =>
  import("./pages/shipments/pending-delivery").then((m) => ({ default: m.PendingDeliveryPage })),
);
const ShipmentDeliveryConfirmPage = lazy(() =>
  import("./pages/shipments/[id]/delivery").then((m) => ({ default: m.ShipmentDeliveryConfirmPage })),
);
const QuotesListPage = lazy(() =>
  import("./pages/quotes").then((m) => ({ default: m.QuotesListPage })),
);
const QuoteDetailPage = lazy(() =>
  import("./pages/quotes/[id]").then((m) => ({ default: m.QuoteDetailPage })),
);
const CostingsListPage = lazy(() =>
  import("./pages/costings").then((m) => ({ default: m.CostingsListPage })),
);
const CostingDetailPage = lazy(() =>
  import("./pages/costings/[id]").then((m) => ({ default: m.CostingDetailPage })),
);
const StockOffersListPage = lazy(() =>
  import("./pages/stock-offers").then((m) => ({ default: m.StockOffersListPage })),
);
const StockOfferDetailPage = lazy(() =>
  import("./pages/stock-offers/[id]").then((m) => ({ default: m.StockOfferDetailPage })),
);
// Email inbox (#471) — the mailbox-wide list + RFC-5322 thread view. Lazy:
// most sessions never open it, and it pulls its own filter/thread chunks.
const EmailsListPage = lazy(() =>
  import("./pages/emails").then((m) => ({ default: m.EmailsListPage })),
);
const EmailThreadPage = lazy(() =>
  import("./pages/emails/[id]").then((m) => ({ default: m.EmailThreadPage })),
);
const DevelopmentsListPage = lazy(() =>
  import("./pages/developments").then((m) => ({ default: m.DevelopmentsListPage })),
);
const DevelopmentDetailPage = lazy(() =>
  import("./pages/developments/[id]").then((m) => ({ default: m.DevelopmentDetailPage })),
);

// Wave 5 — Seasons & SKUs.
const SeasonsListPage = lazy(() =>
  import("./pages/seasons").then((m) => ({ default: m.SeasonsListPage })),
);
const SeasonSkusPage = lazy(() =>
  import("./pages/seasons/[code]/skus").then((m) => ({ default: m.SeasonSkusPage })),
);
const SeasonSkuDetailPage = lazy(() =>
  import("./pages/seasons/[code]/skus/[id]").then((m) => ({ default: m.SeasonSkuDetailPage })),
);

// Heavy/recharts-driven dashboards + supplier scorecard — pull in the
// recharts vendor chunk on demand.
const ProductionDashboardPage = lazy(() =>
  import("./pages/dashboard/production").then((m) => ({ default: m.ProductionDashboardPage })),
);
// Management Overview — cross-functional roll-up (production + supplier +
// QA + meetings). Issue #477, epic #460 gap g16.
const ManagementDashboardPage = lazy(() =>
  import("./pages/dashboard/management").then((m) => ({ default: m.ManagementDashboardPage })),
);
// Reports hub — makes the meeting-export generator reachable outside a
// specific meeting. Issue #477, epic #460 gap g16.
const ReportsPage = lazy(() =>
  import("./pages/reports").then((m) => ({ default: m.ReportsPage })),
);
// My to-do — the signed-in user's missing/stuck items, grouped by stage
// (#1009, Epic #956 phase 3b).
const TodoPage = lazy(() =>
  import("./pages/todo").then((m) => ({ default: m.TodoPage })),
);
const SupplierProfilePage = lazy(() =>
  import("./pages/suppliers/[id]").then((m) => ({ default: m.SupplierProfilePage })),
);
const SupplierScorecardPage = lazy(() =>
  import("./pages/suppliers/scorecard").then((m) => ({ default: m.SupplierScorecardPage })),
);
const QaDashboardPage = lazy(() =>
  import("./pages/qa/dashboard").then((m) => ({ default: m.QaDashboardPage })),
);
// Cross-stage pipeline board (#1008, Epic #956 phase 3a).
const PipelineBoardPage = lazy(() =>
  import("./pages/pipeline").then((m) => ({ default: m.PipelineBoardPage })),
);
// W7.5: TruworthsWorkflowPage retired. The Truworths-specific surface
// (production.truworths_stages, list_truworths_orders_with_stages,
// is_truworths_customer, the 5-stage approval UI) was dropped via migration
// 5760 once 5745 mirrored its data into production.milestones. The route
// /customers/truworths/workflow is removed entirely — there is no clean
// single-customer redirect because three Truworths customers exist
// (TRUWORTHS / TRUWORTHS - FUEL / TRUWORTHS - IDENTITY). Use the universal
// Critical Path at /customers/:id/critical-path instead.

// W7.4: Universal Critical Path — generic per-customer cross-order grid.
const TimelineSettingsPage = lazy(() => import("./pages/admin/timeline-settings").then(m => ({ default: m.TimelineSettingsPage })));
const CustomerCriticalPathPage = lazy(() =>
  import("./pages/customers/[id]/critical-path").then((m) => ({
    default: m.CustomerCriticalPathPage,
  })),
);
const SecuritySettingsPage = lazy(() =>
  import("./pages/settings/security").then((m) => ({ default: m.SecuritySettingsPage })),
);
const AccountMfaPage = lazy(() =>
  import("./pages/account/mfa").then((m) => ({ default: m.AccountMfaPage })),
);
const FactorRulesPage = lazy(() =>
  import("./pages/finance/factor-rules").then((m) => ({ default: m.FactorRulesPage })),
);

// Admin — user management. Pages enforce the admin role via useIsAdmin();
// list_users / get_user / update_user_roles / disable_user verbs are RLS-
// gated server-side too.
const UsersListPage = lazy(() =>
  import("./pages/admin/users").then((m) => ({ default: m.UsersListPage })),
);
const UserDetailPage = lazy(() =>
  import("./pages/admin/users/[id]").then((m) => ({ default: m.UserDetailPage })),
);
const InviteUserPage = lazy(() =>
  import("./pages/admin/users/invite").then((m) => ({ default: m.InviteUserPage })),
);

// Admin — W13 admin CRUD pages.
const SeasonsAdminPage = lazy(() =>
  import("./pages/admin/seasons").then((m) => ({ default: m.SeasonsAdminPage })),
);
// W7.4: real Order Workflow admin page (Critical Path catalog + templates).
// Replaces the AdminOrderWorkflowPlaceholderPage stub from W13 sub-task 4.
const AdminOrderWorkflowPage = lazy(() =>
  import("./pages/admin/order-workflow").then((m) => ({
    default: m.AdminOrderWorkflowPage,
  })),
);
// W14 (Item #8) replaces the AdminSettingsPlaceholderPage with the real
// global Settings page + adds a sibling Notifications page. Both are admin-
// only via useIsAdmin and gated server-side too.
const AdminSettingsPage = lazy(() =>
  import("./pages/admin/settings").then((m) => ({ default: m.AdminSettingsPage })),
);
const AdminNotificationsPage = lazy(() =>
  import("./pages/admin/notifications").then((m) => ({ default: m.AdminNotificationsPage })),
);
// Meeting export templates — admin-managed named column sets per export kind.
const ExportTemplatesAdminPage = lazy(() =>
  import("./pages/admin/export-templates").then((m) => ({ default: m.ExportTemplatesAdminPage })),
);
const AiUsageAdminPage = lazy(() =>
  import("./pages/admin/ai-usage").then((m) => ({ default: m.AiUsageAdminPage })),
);
// #673: size sets, curves and pre-packs — a dedicated admin page (not the
// [Sample sizes] tab in /admin/settings, which models a different thing).
const AdminSizesPage = lazy(() =>
  import("./pages/admin/sizes").then((m) => ({ default: m.AdminSizesPage })),
);
// #794: brand management (create/rename/re-parent/archive) — the last
// unbuilt piece of Epic #740 Phase 3a (#759 shipped the write verbs; no
// screen ever called them until now).
const AdminBrandsPage = lazy(() =>
  import("./pages/admin/brands").then((m) => ({ default: m.AdminBrandsPage })),
);
// #795: "Brand labels" management (create/rename/archive/unarchive) for
// production.labels — the sibling of #794's brand surface, kept as a
// separate route next to it in the sidebar's Admin group rather than a
// tab, since labels have no customer scope or nesting to master-detail over.
const AdminLabelsPage = lazy(() =>
  import("./pages/admin/labels").then((m) => ({ default: m.AdminLabelsPage })),
);
// #1074 (Epic #1072 phase 1b): external parties — receivers, inspectors,
// importers of record, clearing agents, forwarders.
const AdminPartiesPage = lazy(() =>
  import("./pages/admin/parties").then((m) => ({ default: m.AdminPartiesPage })),
);

// Docs — every /docs/* page pulls in the react-markdown + remark/rehype
// plugin chain via <MarkdownRenderer/>. Lazy-loading them keeps the markdown
// pipeline out of the main bundle for staff who never open the help surface.
const DocsIndexPage = lazy(() =>
  import("./pages/docs").then((m) => ({ default: m.DocsIndexPage })),
);
const DocsUserGuidePage = lazy(() =>
  import("./pages/docs/user-guide").then((m) => ({ default: m.DocsUserGuidePage })),
);
const DocsQuickstartPage = lazy(() =>
  import("./pages/docs/quickstart").then((m) => ({ default: m.DocsQuickstartPage })),
);
const DocsGlossaryPage = lazy(() =>
  import("./pages/docs/glossary").then((m) => ({ default: m.DocsGlossaryPage })),
);
const DocsPopiaPage = lazy(() =>
  import("./pages/docs/popia").then((m) => ({ default: m.DocsPopiaPage })),
);
const DocsWorkflowsIndexPage = lazy(() =>
  import("./pages/docs/workflows").then((m) => ({ default: m.DocsWorkflowsIndexPage })),
);
const DocsWorkflowPage = lazy(() =>
  import("./pages/docs/workflows/[slug]").then((m) => ({ default: m.DocsWorkflowPage })),
);

// ── Sample-library lazy imports ────────────────────────────────────────
// Direct dynamic imports — each chunk loads on first navigation. The
// previous defensiveLazy / import.meta.glob fallback was a Phase-7-era
// scaffold for parallel-agent rollout (modules expected to be missing).
// Every page below now ships, so the static import path is both faster
// and survives Vite's bracket-handling in production builds (the glob
// match was silently dropping `[id].tsx` and `[id]/edit.tsx` keys in the
// production manifest, which surfaced as "Library page not available"
// when admins clicked into a sample card).

const LibraryListPage = lazy(() =>
  import("./pages/library/index").then((m) => ({ default: m.LibraryListPage })),
);
const LibraryDetailPage = lazy(() =>
  import("./pages/library/[id]").then((m) => ({ default: m.LibraryDetailPage })),
);
const NewLibraryItemPage = lazy(() =>
  import("./pages/library/new").then((m) => ({ default: m.NewLibraryItemPage })),
);
const EditLibraryItemPage = lazy(() =>
  import("./pages/library/[id]/edit").then((m) => ({ default: m.EditLibraryItemPage })),
);
const LoansListPage = lazy(() =>
  import("./pages/library/loans").then((m) => ({ default: m.LoansListPage })),
);
const LoanDetailPage = lazy(() =>
  import("./pages/library/loans/[id]").then((m) => ({ default: m.LoanDetailPage })),
);
const NewLoanPage = lazy(() =>
  import("./pages/library/loans/new").then((m) => ({ default: m.NewLoanPage })),
);
const StorageLocationsPage = lazy(() =>
  import("./pages/library/locations").then((m) => ({ default: m.StorageLocationsPage })),
);
const TagsAdminPage = lazy(() =>
  import("./pages/library/tags").then((m) => ({ default: m.TagsAdminPage })),
);
const DocTypesAdminPage = lazy(() =>
  import("./pages/admin/doc-types").then((m) => ({ default: m.DocTypesAdminPage })),
);
const ScanSamplePage = lazy(() =>
  import("./pages/library/scan").then((m) => ({ default: m.ScanSamplePage })),
);
const SampleLabelsPage = lazy(() =>
  import("./pages/library/labels").then((m) => ({ default: m.SampleLabelsPage })),
);
const ImportSamplesPage = lazy(() =>
  import("./pages/library/import").then((m) => ({ default: m.ImportSamplesPage })),
);
const ExportSamplesPage = lazy(() =>
  import("./pages/library/export").then((m) => ({ default: m.ExportSamplesPage })),
);
const LibraryDashboardPage = lazy(() =>
  import("./pages/library/dashboard").then((m) => ({ default: m.LibraryDashboardPage })),
);

const AiImagesPage = lazy(() =>
  import("./pages/library/ai-images").then((m) => ({ default: m.AiImagesPage })),
);
const AiImagesGeneratePage = lazy(() =>
  import("./pages/library/ai-images-generate").then((m) => ({ default: m.AiImagesGeneratePage })),
);

const MeetingsListPage = lazy(() =>
  import("./pages/meetings").then((m) => ({ default: m.MeetingsListPage })),
);
const MeetingDetailPage = lazy(() =>
  import("./pages/meetings/[id]").then((m) => ({ default: m.MeetingDetailPage })),
);
const MeetingPickModePage = lazy(() =>
  import("./pages/meetings/[id]/mode").then((m) => ({ default: m.MeetingPickModePage })),
);

// Briefs — per-buyer sample-development briefs (5961-5963). List + detail.
const BriefsListPage = lazy(() =>
  import("./pages/briefs").then((m) => ({ default: m.BriefsListPage })),
);
const BriefDetailPage = lazy(() =>
  import("./pages/briefs/[id]").then((m) => ({ default: m.BriefDetailPage })),
);
// Map unmatched names (#761, Epic #740 Phase 3) — crm.map_aliases-gated,
// lives under /briefs so sample_coordinator (one of the two capability
// holders per ruling D30) can actually reach it.
const UnmatchedNamesPage = lazy(() =>
  import("./pages/briefs/unmatched-names").then((m) => ({ default: m.UnmatchedNamesPage })),
);
// AI documents awaiting confirmation (#1059, Epic #957 Phase 3c) — the queue
// of extracted buyer documents nobody has confirmed yet. Under /briefs so the
// existing /briefs role surface reaches it (same reason as unmatched-names).
const AiConfirmationsPage = lazy(() =>
  import("./pages/briefs/ai-confirmations").then((m) => ({ default: m.AiConfirmationsPage })),
);

// Public share page (#519) — /share/:token, no session required. Lazy so its
// chunk is not part of the authenticated bundle (and vice versa: a visitor
// redeeming a link never downloads the portal app).
const SharePage = lazy(() =>
  import("./pages/share/[token]").then((m) => ({ default: m.SharePage })),
);


// Presentations history — Wave 3 Agent 3B.
// The create page (/library/presentations) is wired by Agent 3A.
const PresentationsHistoryPage = lazy(() =>
  import("./pages/library/presentations/history").then((m) => ({
    default: m.PresentationsHistoryPage,
  })),
);

// W15 (Item #8) — Presentations feature. Agent 3A owns the full page
// implementation; this import resolves to the placeholder until 3A's
// commit lands (same file path, same export name).
const PresentationsPage = lazy(() =>
  import("./pages/library/presentations").then((m) => ({ default: m.PresentationsPage })),
);

// W15 (Item #8) — Library admin hub. Lists all library admin tools
// (Tags, Locations, Import, Export) in one place.
const LibraryAdminPage = lazy(() =>
  import("./pages/library/admin").then((m) => ({ default: m.LibraryAdminPage })),
);

const LibrarySuspense = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
);

// LazyRoute wraps a React.lazy() page in Suspense with a small <Loading/>
// fallback. Used for the heavy/infrequent routes (recharts dashboards, docs,
// admin) declared at the top of this file.
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
);

export function App() {
  useVersionWatcher();

  return (
    <RefineKbarProvider>
      <Refine
        // Refine's Supabase data provider talks REST to PostgREST. It uses the
        // session JWT from supabase-js (set automatically once login succeeds),
        // so RLS is honoured per-user — never service-role from the browser.
        dataProvider={dataProvider(supabase)}
        authProvider={authProvider}
        routerProvider={routerProvider}
        resources={[
          { name: "dashboard",     list: "/dashboard" },
          { name: "actions",       list: "/actions",       show: "/actions/:code" },
          { name: "orders",        list: "/orders",        show: "/orders/:id", create: "/orders/new" },
          { name: "supplier_pos",  list: "/supplier-pos",  show: "/supplier-pos/:id", meta: { label: "Supplier POs" } },
          { name: "customers",     list: "/customers",     show: "/customers/:id" },
          { name: "suppliers",     list: "/suppliers",     show: "/suppliers/:id" },
          { name: "samples",       list: "/samples",       show: "/samples/:id" },
          { name: "qa",            list: "/qa",            show: "/qa/:id" },
          { name: "shipments",     list: "/shipments",     show: "/shipments/:id" },
          { name: "developments",  list: "/developments",  show: "/developments/:id" },
          { name: "emails",        list: "/emails",        show: "/emails/:id" },
          { name: "quotes",        list: "/quotes",        show: "/quotes/:id" },
          { name: "costings",      list: "/costings",      show: "/costings/:id" },
          { name: "stock_offers",  list: "/stock-offers",  show: "/stock-offers/:id" },
          { name: "meetings",      list: "/meetings",      show: "/meetings/:id" },
          { name: "seasons",       list: "/seasons",       meta: { label: "Seasons & SKUs" } },
          {
            name: "library",
            list: "/library",
            show: "/library/:id",
            create: "/library/new",
            edit: "/library/:id/edit",
            meta: { label: "Sample Library", icon: <Library className="h-4 w-4" /> },
          },
          {
            name: "loans",
            list: "/library/loans",
            show: "/library/loans/:id",
            create: "/library/loans/new",
          },
          {
            name: "storage_locations",
            list: "/library/locations",
            meta: { label: "Storage Locations" },
          },
          {
            name: "tags",
            list: "/library/tags",
            meta: { label: "Tags" },
          },
        ]}
        options={{
          disableTelemetry: true,
          warnWhenUnsavedChanges: true,
        }}
      >
        {/* <RefineKbar /> auto-registered list/show/edit/create kbar actions
            for every resource above which created verbose duplicates and
            category headers next to our manually-registered shortcuts.
            CommandPalette.tsx now registers ALL Cmd-K entries explicitly so
            we get a single, curated palette. */}
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/oauth/consent" element={<OAuthConsentPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />

          {/* Share links (#519) — a customer with no account opens this with a
              token in the URL. It MUST stay outside the <Authenticated>
              boundary below: wrapping it would bounce every visitor to /login
              and defeat the entire feature. The page renders nothing until the
              unauthenticated `sample-share` edge function validates the token
              server-side (expiry, revocation, scope), so being public here
              grants no access by itself. */}
          <Route
            path="/share/:token"
            element={
              <LazyRoute>
                <SharePage />
              </LazyRoute>
            }
          />

          {/* MFA challenge — authenticated but pre-AAL2. Sits OUTSIDE the
              MfaGate so the gate can redirect here without an infinite loop. */}
          <Route
            path="/auth/mfa-challenge"
            element={
              <Authenticated
                key="mfa-challenge"
                fallback={<CatchAllNavigate to="/login" />}
              >
                <MfaChallengePage />
              </Authenticated>
            }
          />

          {/* MFA enrollment (mandatory, admins only, #687) — authenticated
              but has zero enrolled TOTP factors. Sits OUTSIDE the MfaGate,
              same reasoning as /auth/mfa-challenge above: the gate needs to
              be able to redirect here without wrapping this route in itself. */}
          <Route
            path="/auth/mfa-enroll"
            element={
              <Authenticated
                key="mfa-enroll"
                fallback={<CatchAllNavigate to="/login" />}
              >
                <MfaEnrollPage />
              </Authenticated>
            }
          />

          {/* Authenticated routes — wrapped in Layout + MfaGate. The
              SampleCoordOnlyGuard sits inside the auth boundary so it has
              access to the user's session via supabase-js, and outside the
              CommandPalette/Layout so it can short-circuit the entire
              render tree to /library before any privileged page mounts.
              Any user whose only operational role is `sample_coordinator`
              gets redirected back to /library if they navigate (or paste a
              URL) to anything outside the allow-listed sample surfaces. */}
          <Route
            element={
              <Authenticated
                key="authenticated-layout"
                fallback={<CatchAllNavigate to="/login" />}
              >
                <MfaGate>
                  <SampleCoordOnlyGuard>
                    <CommandPalette />
                    <Layout />
                  </SampleCoordOnlyGuard>
                </MfaGate>
              </Authenticated>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route
              path="/dashboard/production"
              element={
                <LazyRoute>
                  <ProductionDashboardPage />
                </LazyRoute>
              }
            />
            {/* Management Overview — cross-functional roll-up for Jon/
                Marchant/Hamza (issue #477). */}
            <Route
              path="/dashboard/management"
              element={
                <LazyRoute>
                  <ManagementDashboardPage />
                </LazyRoute>
              }
            />

            {/* Reports hub — meeting-export generator reachable outside a
                specific meeting (issue #477). */}
            <Route
              path="/reports"
              element={
                <LazyRoute>
                  <ReportsPage />
                </LazyRoute>
              }
            />

            {/* My to-do — missing/stuck items grouped by stage (#1009,
                Epic #956 phase 3b). */}
            <Route
              path="/todo"
              element={
                <LazyRoute>
                  <TodoPage />
                </LazyRoute>
              }
            />

            {/* Orders. /orders/new MUST come before /orders/:id so the
                static segment is matched first. */}
            <Route path="/orders" element={<OrdersListPage />} />
            <Route
              path="/orders/new"
              element={<LazyRoute><NewOrderForm /></LazyRoute>}
            />
            <Route
              path="/orders/:id"
              element={<LazyRoute><OrderDetailPage /></LazyRoute>}
            />

            {/* Supplier POs (#1065, Epic #1063 phase 1b). A PPO is per
                SUPPLIER and carries blocks from many customer SOs, so it is
                its own list rather than a sub-route of /orders. */}
            <Route
              path="/supplier-pos"
              element={<LazyRoute><SupplierPosListPage /></LazyRoute>}
            />
            <Route
              path="/supplier-pos/:id"
              element={<LazyRoute><SupplierPoDetailPage /></LazyRoute>}
            />

            {/* Cross-stage pipeline board (#1008, Epic #956 phase 3a). */}
            <Route
              path="/pipeline"
              element={<LazyRoute><PipelineBoardPage /></LazyRoute>}
            />

            {/* Action inbox */}
            <Route path="/actions" element={<ActionsInboxPage />} />
            <Route
              path="/actions/:code"
              element={<LazyRoute><ActionDetailPage /></LazyRoute>}
            />

            {/* CRM — customers + suppliers */}
            <Route path="/customers" element={<CustomersListPage />} />
            {/* W7.4: Universal Critical Path — must come before /customers/:id
                so the static segment matches first. (W7.5: the legacy
                /customers/truworths/workflow route was removed; muscle-memory
                hits now fall through to the global 404.) */}
            <Route
              path="/customers/:id/critical-path"
              element={<LazyRoute><CustomerCriticalPathPage /></LazyRoute>}
            />
            <Route
              path="/customers/:id"
              element={<LazyRoute><CustomerProfilePage /></LazyRoute>}
            />
            <Route path="/suppliers" element={<SuppliersListPage />} />
            <Route
              path="/suppliers/scorecard"
              element={<LazyRoute><SupplierScorecardPage /></LazyRoute>}
            />
            <Route
              path="/suppliers/:id"
              element={<LazyRoute><SupplierProfilePage /></LazyRoute>}
            />

            {/* Production group */}
            <Route
              path="/samples"
              element={<LazyRoute><SamplesListPage /></LazyRoute>}
            />
            <Route
              path="/samples/:id"
              element={<LazyRoute><SampleDetailPage /></LazyRoute>}
            />
            <Route
              path="/qa"
              element={<LazyRoute><QaListPage /></LazyRoute>}
            />
            {/* /qa/pending and /qa/dashboard MUST come before /qa/:id so the
                slug doesn't match those literal segments. */}
            <Route
              path="/qa/pending"
              element={<LazyRoute><QaPendingPage /></LazyRoute>}
            />
            <Route
              path="/qa/dashboard"
              element={<LazyRoute><QaDashboardPage /></LazyRoute>}
            />
            <Route
              path="/qa/:id"
              element={<LazyRoute><QaDetailPage /></LazyRoute>}
            />
            <Route
              path="/shipments"
              element={<LazyRoute><ShipmentsListPage /></LazyRoute>}
            />
            <Route
              path="/shipments/pending-delivery"
              element={<LazyRoute><PendingDeliveryPage /></LazyRoute>}
            />
            <Route
              path="/shipments/:id/delivery"
              element={<LazyRoute><ShipmentDeliveryConfirmPage /></LazyRoute>}
            />
            <Route
              path="/shipments/:id"
              element={<LazyRoute><ShipmentDetailPage /></LazyRoute>}
            />
            <Route
              path="/developments"
              element={<LazyRoute><DevelopmentsListPage /></LazyRoute>}
            />
            <Route
              path="/developments/:id"
              element={<LazyRoute><DevelopmentDetailPage /></LazyRoute>}
            />

            {/* Email inbox (#471) */}
            <Route
              path="/emails"
              element={<LazyRoute><EmailsListPage /></LazyRoute>}
            />
            <Route
              path="/emails/:id"
              element={<LazyRoute><EmailThreadPage /></LazyRoute>}
            />

            {/* Seasons & SKUs */}
            <Route
              path="/seasons"
              element={<LazyRoute><SeasonsListPage /></LazyRoute>}
            />
            <Route
              path="/seasons/:code/skus"
              element={<LazyRoute><SeasonSkusPage /></LazyRoute>}
            />
            <Route
              path="/seasons/:code/skus/:id"
              element={<LazyRoute><SeasonSkuDetailPage /></LazyRoute>}
            />

            {/* Library group */}
            <Route
              path="/quotes"
              element={<LazyRoute><QuotesListPage /></LazyRoute>}
            />
            <Route
              path="/quotes/:id"
              element={<LazyRoute><QuoteDetailPage /></LazyRoute>}
            />
            <Route
              path="/costings"
              element={<LazyRoute><CostingsListPage /></LazyRoute>}
            />
            <Route
              path="/costings/:id"
              element={<LazyRoute><CostingDetailPage /></LazyRoute>}
            />
            <Route
              path="/stock-offers"
              element={<LazyRoute><StockOffersListPage /></LazyRoute>}
            />
            <Route
              path="/stock-offers/:id"
              element={<LazyRoute><StockOfferDetailPage /></LazyRoute>}
            />

            {/* Meetings (lazy via defensiveLazy + meetings glob) */}
            <Route
              path="/meetings"
              element={
                <LibrarySuspense>
                  <MeetingsListPage />
                </LibrarySuspense>
              }
            />
            <Route
              path="/meetings/:id"
              element={
                <LibrarySuspense>
                  <MeetingDetailPage />
                </LibrarySuspense>
              }
            />

            {/* Briefs — per-buyer sample-development briefs. List (kanban ↔
                table) + detail. Gated by the /briefs role surface in
                useUserRoles; the brief verbs are RLS-gated server-side too. */}
            <Route
              path="/briefs"
              element={
                <LibrarySuspense>
                  <BriefsListPage />
                </LibrarySuspense>
              }
            />
            {/* Map unmatched names (#761) — static segment, must come before
                /briefs/:id so it isn't swallowed by the dynamic route. */}
            <Route
              path="/briefs/unmatched-names"
              element={
                <LibrarySuspense>
                  <UnmatchedNamesPage />
                </LibrarySuspense>
              }
            />
            {/* AI documents awaiting confirmation (#1059) — static segment,
                must come before /briefs/:id so it isn't swallowed by the
                dynamic route. */}
            <Route
              path="/briefs/ai-confirmations"
              element={
                <LibrarySuspense>
                  <AiConfirmationsPage />
                </LibrarySuspense>
              }
            />
            <Route
              path="/briefs/:id"
              element={
                <LibrarySuspense>
                  <BriefDetailPage />
                </LibrarySuspense>
              }
            />

            {/* AI Images console — runs, review queue, stale images.
                Must come before /library/:id so the static segment
                "ai-images" is matched first. */}
            <Route
              path="/library/ai-images"
              element={
                <LibrarySuspense>
                  <AiImagesPage />
                </LibrarySuspense>
              }
            />
            {/* AI images Generate tab — selection-first browser. Must come
                before /library/:id so the static segment is matched first. */}
            <Route
              path="/library/ai-images/generate"
              element={
                <LibrarySuspense>
                  <AiImagesGeneratePage />
                </LibrarySuspense>
              }
            />

            {/* W15 (Item #8) — Presentations (primary Library nav entry). Agent
                3A owns the full create-page implementation; the route renders
                the real component once 3A's file is present. */}
            <Route
              path="/library/presentations"
              element={
                <LibrarySuspense>
                  <PresentationsPage />
                </LibrarySuspense>
              }
            />
            {/* Presentations history — Wave 3 Agent 3B.
                Must come before /library/:id so the static segment
                "presentations" is matched first. */}
            <Route
              path="/library/presentations/history"
              element={
                <LibrarySuspense>
                  <PresentationsHistoryPage />
                </LibrarySuspense>
              }
            />

            {/* W15 (Item #8) — Library admin hub. */}
            <Route
              path="/library/admin"
              element={
                <LibrarySuspense>
                  <LibraryAdminPage />
                </LibrarySuspense>
              }
            />

            {/* Sample library — pages landing in parallel via defensiveLazy.
                If the page file doesn't exist yet, defensiveLazy renders
                <LibraryNotReadyPage/> instead of crashing the bundle. */}
            <Route
              path="/library"
              element={
                <LibrarySuspense>
                  <LibraryListPage />
                </LibrarySuspense>
              }
            />
            <Route
              path="/library/new"
              element={
                <LibrarySuspense>
                  <NewLibraryItemPage />
                </LibrarySuspense>
              }
            />
            <Route
              path="/library/dashboard"
              element={
                <LibrarySuspense>
                  <LibraryDashboardPage />
                </LibrarySuspense>
              }
            />
            <Route
              path="/library/scan"
              element={
                <LibrarySuspense>
                  <ScanSamplePage />
                </LibrarySuspense>
              }
            />
            <Route
              path="/library/labels"
              element={
                <LibrarySuspense>
                  <SampleLabelsPage />
                </LibrarySuspense>
              }
            />
            <Route
              path="/library/import"
              element={
                <LibrarySuspense>
                  <ImportSamplesPage />
                </LibrarySuspense>
              }
            />
            <Route
              path="/library/export"
              element={
                <LibrarySuspense>
                  <ExportSamplesPage />
                </LibrarySuspense>
              }
            />
            <Route
              path="/library/locations"
              element={
                <LibrarySuspense>
                  <StorageLocationsPage />
                </LibrarySuspense>
              }
            />
            <Route
              path="/library/tags"
              element={
                <LibrarySuspense>
                  <TagsAdminPage />
                </LibrarySuspense>
              }
            />
            <Route
              path="/library/loans"
              element={
                <LibrarySuspense>
                  <LoansListPage />
                </LibrarySuspense>
              }
            />
            <Route
              path="/library/loans/new"
              element={
                <LibrarySuspense>
                  <NewLoanPage />
                </LibrarySuspense>
              }
            />
            <Route
              path="/library/loans/:id"
              element={
                <LibrarySuspense>
                  <LoanDetailPage />
                </LibrarySuspense>
              }
            />
            {/* /library/:id and /library/:id/edit MUST come after the more
                specific routes above (otherwise React Router matches the
                slug to "new", "scan", etc.). */}
            <Route
              path="/library/:id/edit"
              element={
                <LibrarySuspense>
                  <EditLibraryItemPage />
                </LibrarySuspense>
              }
            />
            <Route
              path="/library/:id"
              element={
                <LibrarySuspense>
                  <LibraryDetailPage />
                </LibrarySuspense>
              }
            />

            {/* Sample-library meeting picking mode */}
            <Route
              path="/meetings/:id/mode"
              element={
                <LibrarySuspense>
                  <MeetingPickModePage />
                </LibrarySuspense>
              }
            />

            {/* Structured write forms */}
            <Route
              path="/forms"
              element={<LazyRoute><FormsHubPage /></LazyRoute>}
            />
            <Route
              path="/forms/new-customer"
              element={<LazyRoute><NewCustomerForm /></LazyRoute>}
            />
            <Route
              path="/forms/log-meeting"
              element={<LazyRoute><LogMeetingForm /></LazyRoute>}
            />
            <Route
              path="/forms/new-sample"
              element={<LazyRoute><NewSampleForm /></LazyRoute>}
            />
            <Route
              path="/forms/qa-result"
              element={<LazyRoute><QaResultForm /></LazyRoute>}
            />
            <Route
              path="/forms/new-quote"
              element={<LazyRoute><NewQuoteForm /></LazyRoute>}
            />
            <Route
              path="/forms/new-po"
              element={<LazyRoute><NewPoForm /></LazyRoute>}
            />

            {/* Settings */}
            <Route
              path="/settings/security"
              element={
                <LazyRoute>
                  <SecuritySettingsPage />
                </LazyRoute>
              }
            />

            {/* Account — self-service security surfaces (MFA enrolment +
                management). Visible to every authenticated user; reachable
                from the top-bar user menu and Cmd-K. */}
            <Route
              path="/account/mfa"
              element={
                <LazyRoute>
                  <AccountMfaPage />
                </LazyRoute>
              }
            />

            {/* Finance — admin pages. Page itself enforces admin role via
                useIsAdmin(); RLS on finance.factor_rules also gates writes. */}
            <Route
              path="/finance/factor-rules"
              element={
                <LazyRoute>
                  <FactorRulesPage />
                </LazyRoute>
              }
            />

            {/* Admin — user management. /invite must come before /:id so the
                literal "invite" segment isn't matched as a user id. Pages
                enforce admin via useIsAdmin(); the verbs are RLS-gated too. */}
            <Route
              path="/admin/users"
              element={
                <LazyRoute>
                  <UsersListPage />
                </LazyRoute>
              }
            />
            <Route
              path="/admin/users/invite"
              element={
                <LazyRoute>
                  <InviteUserPage />
                </LazyRoute>
              }
            />
            <Route
              path="/admin/users/:id"
              element={
                <LazyRoute>
                  <UserDetailPage />
                </LazyRoute>
              }
            />

            {/* W13 — admin CRUD surfaces. Pages enforce admin via useIsAdmin();
                writes go through admin-gated cc-os-mcp verbs (requireRole). */}
            <Route
              path="/admin/seasons"
              element={
                <LazyRoute>
                  <SeasonsAdminPage />
                </LazyRoute>
              }
            />

            {/* /admin/* aliases for existing /library/* admin surfaces.
                Renders the SAME components — no redirect — so deep-linkable
                URLs work in both forms. The original /library/* routes stay
                intact (existing bookmarks). */}
            <Route
              path="/admin/tags"
              element={<LibrarySuspense><TagsAdminPage /></LibrarySuspense>}
            />
            <Route
              path="/admin/doc-types"
              element={<LibrarySuspense><DocTypesAdminPage /></LibrarySuspense>}
            />
            <Route
              path="/admin/locations"
              element={<LibrarySuspense><StorageLocationsPage /></LibrarySuspense>}
            />

            {/* W7.4: Order Workflow — Critical Path catalog + per-order-type templates. */}
            <Route
              path="/admin/order-workflow"
              element={<LazyRoute><AdminOrderWorkflowPage /></LazyRoute>}
            />
            <Route path="/admin/timeline-settings" element={<LazyRoute><TimelineSettingsPage /></LazyRoute>} />
            {/* W14 (Item #8) — Admin Settings + Notifications. */}
            <Route
              path="/admin/settings"
              element={<LazyRoute><AdminSettingsPage /></LazyRoute>}
            />
            <Route
              path="/admin/notifications"
              element={<LazyRoute><AdminNotificationsPage /></LazyRoute>}
            />
            <Route
              path="/admin/export-templates"
              element={<LazyRoute><ExportTemplatesAdminPage /></LazyRoute>}
            />
            <Route
              path="/admin/ai-usage"
              element={<LazyRoute><AiUsageAdminPage /></LazyRoute>}
            />
            {/* #673: size sets, curves, pre-packs. */}
            <Route
              path="/admin/sizes"
              element={<LazyRoute><AdminSizesPage /></LazyRoute>}
            />
            {/* #794: brand management — create/rename/re-parent/archive. */}
            <Route
              path="/admin/brands"
              element={<LazyRoute><AdminBrandsPage /></LazyRoute>}
            />
            {/* #795: brand-labels management — create/rename/archive/unarchive. */}
            <Route
              path="/admin/labels"
              element={<LazyRoute><AdminLabelsPage /></LazyRoute>}
            />
            {/* #1074: parties management — receivers, inspectors, importers of
                record, clearing agents, forwarders. */}
            <Route
              path="/admin/parties"
              element={<LazyRoute><AdminPartiesPage /></LazyRoute>}
            />

            {/* Docs — in-app /docs surface backed by markdown copied from
                bot-deploy/cc-os/docs at build time (see scripts/copy-docs.mjs).
                Visible to every authenticated user — no role gate. Lazy-
                loaded to keep the react-markdown + remark/rehype chain out
                of the main bundle. */}
            <Route
              path="/docs"
              element={
                <LazyRoute>
                  <DocsIndexPage />
                </LazyRoute>
              }
            />
            <Route
              path="/docs/user-guide"
              element={
                <LazyRoute>
                  <DocsUserGuidePage />
                </LazyRoute>
              }
            />
            <Route
              path="/docs/quickstart"
              element={
                <LazyRoute>
                  <DocsQuickstartPage />
                </LazyRoute>
              }
            />
            <Route
              path="/docs/glossary"
              element={
                <LazyRoute>
                  <DocsGlossaryPage />
                </LazyRoute>
              }
            />
            <Route
              path="/docs/popia"
              element={
                <LazyRoute>
                  <DocsPopiaPage />
                </LazyRoute>
              }
            />
            <Route
              path="/docs/workflows"
              element={
                <LazyRoute>
                  <DocsWorkflowsIndexPage />
                </LazyRoute>
              }
            />
            <Route
              path="/docs/workflows/:slug"
              element={
                <LazyRoute>
                  <DocsWorkflowPage />
                </LazyRoute>
              }
            />
          </Route>

          {/* Catch-all: route through <Authenticated/> — logged-in users land on
              /dashboard, anyone else gets bounced to /login. */}
          <Route
            element={
              <Authenticated key="catch-all" fallback={<Outlet />}>
                <Navigate to="/dashboard" replace />
              </Authenticated>
            }
          >
            <Route path="*" element={<CatchAllNavigate to="/login" />} />
          </Route>
        </Routes>

        <Toaster richColors position="top-right" duration={4000} />
      </Refine>
    </RefineKbarProvider>
  );
}
